import { useState, useCallback } from 'react';
import { Input } from '@/components/ui/input';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import { ChevronDownIcon } from '../icons';
import { FIELD_INVALID_PAINT, FIELD_PAINT } from '@/components/ui/field-surface';
import { cn } from '@/lib/utils';

interface CountryCode {
  code: string;
  dialCode: string;
  flag: string;
  name: string;
}

interface PhoneInputProps {
  value: string;
  onChange: (value: string) => void;
  invalid?: boolean;
}

const COUNTRIES: CountryCode[] = [
  { code: 'SG', dialCode: '+65', flag: '🇸🇬', name: 'Singapore' },
  { code: 'MY', dialCode: '+60', flag: '🇲🇾', name: 'Malaysia' },
  { code: 'ID', dialCode: '+62', flag: '🇮🇩', name: 'Indonesia' },
  { code: 'TH', dialCode: '+66', flag: '🇹🇭', name: 'Thailand' },
  { code: 'PH', dialCode: '+63', flag: '🇵🇭', name: 'Philippines' },
  { code: 'CN', dialCode: '+86', flag: '🇨🇳', name: 'China' },
  { code: 'IN', dialCode: '+91', flag: '🇮🇳', name: 'India' },
  { code: 'US', dialCode: '+1', flag: '🇺🇸', name: 'United States' },
  { code: 'GB', dialCode: '+44', flag: '🇬🇧', name: 'United Kingdom' },
  { code: 'AU', dialCode: '+61', flag: '🇦🇺', name: 'Australia' },
];

export function PhoneInput({ value, onChange, invalid = false }: PhoneInputProps) {
  const [selectedCountry, setSelectedCountry] = useState<CountryCode>(COUNTRIES[0]);

  // Extract local number from full E.164 value
  const localNumber = value?.replace(selectedCountry.dialCode, '') ?? '';

  const handleNumberChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    const number = value?.replace(/\D/g, '') ?? ''; // Only digits
    onChange(`${selectedCountry.dialCode}${number}`);
  }, [selectedCountry.dialCode, onChange]);

  const handleCountryChange = useCallback((country: CountryCode) => {
    const digits = value ? value.replace(/\D/g, '') : '';
    const currentCodeDigits = selectedCountry.dialCode.replace(/\D/g, '');
    const localDigits = digits.startsWith(currentCodeDigits)
      ? digits.slice(currentCodeDigits.length)
      : digits;

    setSelectedCountry(country);
    onChange(`${country.dialCode}${localDigits}`);
  }, [value, selectedCountry.dialCode, onChange]);

  return (
    <div className="flex w-full h-[45px]">
      <DropdownMenu>
        <DropdownMenuTrigger className={cn(FIELD_PAINT, "h-[45px] border rounded-l-input px-5 flex items-center gap-2 w-32 font-inter text-sm hover:bg-filled-1 transition-colors", invalid && FIELD_INVALID_PAINT)}>
          <span className="text-lg leading-none">{selectedCountry.flag}</span>
          <span className="text-off-black">{selectedCountry.dialCode}</span>
          <ChevronDownIcon className="size-4 text-grey ml-auto" />
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="min-w-[250px]">
          {COUNTRIES.map((country) => (
            <DropdownMenuItem
              key={country.code}
              onClick={() => handleCountryChange(country)}
              className="flex items-center gap-3 cursor-pointer"
            >
              <span className="text-lg">{country.flag}</span>
              <span className="flex-1 font-inter text-sm">{country.name}</span>
              <span className="font-inter text-sm text-grey">{country.dialCode}</span>
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>

      <Input
        type="tel"
        value={localNumber}
        onChange={handleNumberChange}
        placeholder="9123 4567"
        aria-invalid={invalid || undefined}
        className={cn(FIELD_PAINT, "h-[45px] rounded-l-none rounded-r-input border-l-0 px-5 font-inter text-sm placeholder:text-grey-light")}
      />
    </div>
  );
}
